import { Notification, useMessagedAsyncFn } from "@payping/web-components";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { terminalManagerApis } from "@payping/api";
import { AddShaparakIbansRequest, IBANItem } from "../types";
import { BusinessIbanApi } from "./businessIbanApi";

interface UseIBANOperationsProps {
  approvedIBANList: IBANItem[];
  pendingIBANList: IBANItem[];
  rejectedIBANList: IBANItem[];
  activeIBAN: string | null;
  onRefresh: () => void;
}

export const useIBANOperations = ({
  approvedIBANList,
  pendingIBANList,
  rejectedIBANList,
  activeIBAN,
  onRefresh,
}: UseIBANOperationsProps) => {
  const { t } = useAppTranslation();

  const [notification, contextHolder] = Notification.useNotification({
    top: 16,
    duration: 5,
    maxCount: 1,
    placement: "top",
  });

  const [addIbansState, addIbans] = useMessagedAsyncFn(
    async (request: AddShaparakIbansRequest) => {
      try {
        const response = await terminalManagerApis.addShaparakIbans(request);
        notification.success({
          message: t("profile.bank.ibanAddedSuccessfully"),
          closable: true,
        });
        return response;
      } catch (error: any) {
        const errorMessage = t("profile.bank.errorAddingIBAN");

        notification.error({
          message: errorMessage,
          closable: true,
        });

        throw error;
      }
    },
    [],
    undefined,
    {},
  );

  const [removeIbansState, removeIbans] = useMessagedAsyncFn(
    async (iban: string) => {
      try {
        const response = await BusinessIbanApi.deleteBusinessIban(iban);
        await onRefresh();
        notification.success({
          message: t("profile.bank.ibanRemovedSuccessfully"),
          closable: true,
        });
        return response;
      } catch (error: any) {
        const errorMessage = error?.message || t("profile.bank.errorRemovingIBAN");
        notification.error({
          message: errorMessage,
          closable: true,
        });
        throw error;
      }
    },
    [],
    undefined,
    { useProgress: true },
  );

  const [retryLastRejectedRequestState, retryLastRejectedRequest] = useMessagedAsyncFn(
    async () => {
      const response = await terminalManagerApis.retryLastRejectedRequest();

      await onRefresh();

      notification.success({
        message: t("profile.bank.retryRequestSuccess"),
        closable: true,
      });

      return response;
    },
    [],
    undefined,
    { useProgress: true },
  );

  const handleAddIBAN = async (
    values: { title: string; iban: string } | { ibans: { title: string; iban: string }[] },
  ) => {
    try {
      let request: AddShaparakIbansRequest;
      let ibansToAdd: string[] = [];

      if ("ibans" in values) {
        const filteredIbans = values.ibans.filter((entry) => entry.iban && entry.iban.trim() !== "");

        // Prevent sending empty array
        if (filteredIbans.length === 0) {
          notification.error({
            message: t("profile.bank.errorAddingIBAN"),
            description: t("profile.bank.atLeastOneIbanRequired"),
            closable: true,
          });
          return false;
        }

        // Normalize IBANs: remove existing IR prefix if present, then add it
        const normalizeIban = (iban: string) => {
          const cleaned = iban.trim().toUpperCase();
          return cleaned.startsWith("IR") ? cleaned : `IR${cleaned}`;
        };

        ibansToAdd = filteredIbans.map((entry) => normalizeIban(entry.iban));
        request = {
          ibans: filteredIbans.map((entry) => ({
            iban: normalizeIban(entry.iban),
            description: entry.title,
          })),
        };
      } else {
        // Validate single IBAN is not empty
        if (!values.iban || values.iban.trim() === "") {
          notification.error({
            message: t("profile.bank.errorAddingIBAN"),
            description: t("profile.bank.ibanRequired"),
            closable: true,
          });
          return false;
        }

        // Normalize IBAN: remove existing IR prefix if present, then add it
        const normalizeIban = (iban: string) => {
          const cleaned = iban.trim().toUpperCase();
          return cleaned.startsWith("IR") ? cleaned : `IR${cleaned}`;
        };

        const normalizedIban = normalizeIban(values.iban);
        ibansToAdd = [normalizedIban];
        request = {
          ibans: [
            {
              iban: normalizedIban,
              description: values.title,
            },
          ],
        };
      }

      // Check for duplicate IBANs in all lists (approved, pending, rejected)
      const allExistingIbans = [
        ...approvedIBANList.map((item) => item.iban),
        ...pendingIBANList.map((item) => item.iban),
        ...rejectedIBANList.map((item) => item.iban),
      ];

      const duplicateIbans = ibansToAdd.filter((iban) => allExistingIbans.includes(iban));

      if (duplicateIbans.length > 0) {
        notification.error({
          message: t("profile.bank.duplicateIbanError"),
          description: duplicateIbans.join(", "),
          closable: true,
        });
        return false;
      }

      const result = await addIbans(request);
      if (result !== null) {
        await onRefresh();
        return true;
      }
      return false;
    } catch (error) {
      // Error is already handled in addIbans function
      return false;
    }
  };

  const handleRemoveIBAN = async (ibanId: string) => {
    const iban =
      approvedIBANList.find((item) => item.id === ibanId) ||
      pendingIBANList.find((item) => item.id === ibanId) ||
      rejectedIBANList.find((item) => item.id === ibanId);
    if (!iban) return;

    // Check if this IBAN is the active IBAN
    if (activeIBAN && iban.iban === activeIBAN) {
      notification.error({
        message: t("profile.bank.cannotDeleteActiveIBAN"),
        closable: true,
      });
      return;
    }

    try {
      await removeIbans(iban.iban);
      onRefresh();
    } catch (error) {
      // Error is already handled in removeIbans function
    }
  };

  return {
    handleAddIBAN,
    handleRemoveIBAN,
    retryLastRejectedRequest,
    loading: addIbansState.loading || removeIbansState.loading || retryLastRejectedRequestState.loading,
    error: addIbansState.error || removeIbansState.error || retryLastRejectedRequestState.error,
    retryRequestLoading: retryLastRejectedRequestState.loading,
    contextHolder,
  };
};
